/**
 * Buyers — the client list seen through what they have bought.
 *
 * **The rule that makes this a screen rather than a relabelled contact book.** The operator: *"A
 * buyer without a purchase is definitely not a buyer but a client. So they shouldn't belong on that
 * list."* Somebody who enquired and is being chased is a prospect; they appear here the moment a
 * reservation or a sale exists against their name, and not before. Item 13 of
 * `14-DEVELOPMENT-MODULE-PLAN.md`.
 *
 * **That is why the list is built from the sales rather than from `/parties`.** A buyer is defined by
 * having a purchase, so the sales are the authority on who is one — asking the client list and then
 * filtering it would be asking the wrong question and correcting the answer. It has a second
 * consequence that the operator asked for by name: *"Yes honour that setting, and ensure it actually
 * takes effect."* `GET /purchases` and `GET /reservations` are already confined to the development
 * book and to the caller's own schemes, so **an agency keeping its two books apart sees buyers only
 * here, by construction** rather than by a filter somebody has to remember. ADR-0130 and
 * `npm run test:module-isolation` are the proof of the endpoints; this screen inherits it.
 *
 * The list reads **Buyer · Properties · Units held · Balance outstanding · Next payment**, which is
 * the operator's own set.
 */

import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useSoldNoun } from "@/lib/development";
import { salesLink, type SaleRow } from "@/lib/sales";
import { useSales, StageBadge } from "./DevelopmentSales";
import {
  Badge,
  Card,
  CardTitle,
  Detail,
  EmptyState,
  ErrorState,
  LinkButton,
  PageHeader,
  Spinner,
  Table,
} from "@/components/ui";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, useLocalPage } from "@/lib/list-params";

/** One buyer, as this screen's rows are made: a person and everything they have bought. */
interface Buyer {
  id: string;
  name: string;
  phone: string | null;
  sales: SaleRow[];
  /** Distinct Properties they have bought into. */
  properties: string[];
  /** How many units they actually hold — an unallocated sale is not a unit held. */
  unitsHeld: number;
  outstanding: number;
  arrears: number;
  nextDueDate: string | null;
  nextDueAmount: number | null;
}

/**
 * Every buyer, from every sale.
 *
 * Grouped here rather than asked of the server, for the reason at the top of the file: the sales are
 * what define a buyer, and they are already in hand for the Sales list and the Overview. One fetch
 * serves three screens and the three cannot disagree about who is a buyer.
 */
export function useBuyers() {
  const sales = useSales();

  const buyers = useMemo(() => {
    const byId = new Map<string, Buyer>();
    for (const row of sales.rows) {
      /* A cancelled sale does not make somebody a buyer — it is the record of one who stopped
         being one — but it is kept on a buyer who has other sales, because their history is the
         thing this screen exists to show. */
      let buyer = byId.get(row.buyerId);
      if (!buyer) {
        buyer = {
          id: row.buyerId,
          name: row.buyerName,
          phone: row.buyerPhone,
          sales: [],
          properties: [],
          unitsHeld: 0,
          outstanding: 0,
          arrears: 0,
          nextDueDate: null,
          nextDueAmount: null,
        };
        byId.set(row.buyerId, buyer);
      }
      buyer.sales.push(row);
      if (!buyer.properties.includes(row.estateName)) buyer.properties.push(row.estateName);
      if (row.unitLabel && row.stage !== "cancelled") buyer.unitsHeld += 1;
      buyer.outstanding += row.outstanding;
      buyer.arrears += row.arrears;
      /* The soonest of their next payments. A buyer with three plans is chased once, on the
         earliest date, and a row showing the latest of the three would be the wrong number. */
      if (row.nextDueDate && (!buyer.nextDueDate || row.nextDueDate < buyer.nextDueDate)) {
        buyer.nextDueDate = row.nextDueDate;
        buyer.nextDueAmount = row.nextDueAmount;
      }
    }
    return [...byId.values()].filter((buyer) => buyer.sales.length > 0).sort((a, b) => a.name.localeCompare(b.name));
  }, [sales.rows]);

  return { ...sales, buyers };
}

export function BuyersPage() {
  const list = useListParams();
  const fmt = useFormat();
  const noun = useSoldNoun();
  const { buyers, isLoading, error, refetch } = useBuyers();

  const shown = useMemo(() => {
    const text = (list.q ?? "").trim().toLowerCase();
    if (!text) return buyers;
    return buyers.filter(
      (buyer) => buyer.name.toLowerCase().includes(text) || (buyer.phone ?? "").toLowerCase().includes(text),
    );
  }, [buyers, list.q]);

  const page = useLocalPage(shown, 20);

  return (
    <div>
      <PageHeader
        title="Buyers"
        subtitle={`${shown.length} ${shown.length === 1 ? "buyer" : "buyers"}`}
        description="Everybody who has reserved or bought. Somebody who has only enquired is a client, and is on the client list."
      />

      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-200 p-3">
          <div className="min-w-[16rem] flex-1">
            <SearchBox params={list} placeholder="Search buyers by name or telephone…" />
          </div>
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={refetch} />
        ) : !shown.length ? (
          <EmptyState
            title={list.q ? `No buyers matched “${list.q}”.` : "No buyers recorded."}
            hint="A client becomes a buyer the moment a reservation or a sale is recorded against their name."
            action={
              <LinkButton to="/app/development/sales/new" need="lease.edit">
                Record a sale
              </LinkButton>
            }
          />
        ) : (
          <>
            <Table
              head={
                <tr>
                  <th>Buyer</th>
                  <th>Properties</th>
                  <th className="num">{titleCase(noun.many)} held</th>
                  <th className="num">Balance outstanding</th>
                  <th>Next payment</th>
                </tr>
              }
            >
              {page.rows.map((buyer) => (
                <tr key={buyer.id}>
                  <td>
                    <Link
                      className="font-medium text-brand-700 hover:underline"
                      to={`/app/development/buyers/${buyer.id}`}
                    >
                      {buyer.name}
                    </Link>
                    <div className="text-xs text-ink-500">
                      {buyer.sales.length === 1 ? "1 sale" : `${buyer.sales.length} sales`}
                    </div>
                  </td>
                  <td className="text-ink-600">
                    {/* Three, and then a count. A buyer with eighty holdings would otherwise be one
                        row eighty names wide, and the column that follows it unreadable. */}
                    {buyer.properties.slice(0, 3).join(", ")}
                    {buyer.properties.length > 3 ? (
                      <span className="text-ink-400"> and {buyer.properties.length - 3} more</span>
                    ) : null}
                  </td>
                  <td className="num">{buyer.unitsHeld}</td>
                  <td className="num">
                    <div className={`tabular font-medium ${buyer.arrears > 0 ? "text-danger-600" : ""}`}>
                      {fmt.money(buyer.outstanding)}
                    </div>
                    {buyer.arrears > 0 ? (
                      <div className="text-xs text-danger-600">{fmt.money(buyer.arrears)} overdue</div>
                    ) : null}
                  </td>
                  <td className="text-ink-600">
                    {buyer.nextDueDate ? (
                      <>
                        {buyer.nextDueAmount !== null ? (
                          <div className="tabular font-medium">{fmt.money(buyer.nextDueAmount)}</div>
                        ) : null}
                        <div className={buyer.nextDueAmount !== null ? "text-xs text-ink-500" : undefined}>
                          {fmt.date(buyer.nextDueDate)}
                        </div>
                      </>
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            <Pagination meta={page.meta} params={page.params} noun="buyers" />
          </>
        )}
      </Card>
    </div>
  );
}

/* --------------------------------------------------------------- one buyer */

interface PartyRecord {
  party: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
    address: string | null;
    statusName?: string | null;
  };
}

/**
 * One buyer: their sales with stages, what has been paid, what is due, and whether they can sign in.
 *
 * Deliberately **not** the generic client record with a different heading, which is what "Buyers"
 * used to open and is why the screen read as nothing. What a developer wants about a buyer is the
 * state of the money and the state of the paperwork; the contact details are one panel, not the
 * subject.
 */
export function BuyerDetailPage() {
  const { id = "" } = useParams();
  const fmt = useFormat();
  const noun = useSoldNoun();
  const { buyers, isLoading, error, refetch } = useBuyers();

  const record = useQuery({
    queryKey: ["party", id],
    queryFn: () => api.get<PartyRecord>(`/parties/${id}`),
  });

  /* Whether they can sign in and watch their own plan. A fact about the person rather than about a
     sale, so it is asked once here rather than on each of their sales. It is allowed to fail
     quietly: somebody who may see buyers but not clients gets a refusal, and a missing line is a
     better outcome than a screen that will not draw. */
  const portal = useQuery({
    queryKey: ["party", id, "portal-account"],
    queryFn: () => api.get<{ linked: boolean; account: { email: string; confirmed: boolean } | null }>(
      `/parties/${id}/portal-account`,
    ),
    retry: false,
  });

  const buyer = buyers.find((row) => row.id === id);

  if (isLoading || record.isLoading) return <Spinner />;
  if (error || record.error) return <ErrorState error={error ?? record.error} retry={() => { refetch(); void record.refetch(); }} />;

  const party = record.data?.party;

  return (
    <div>
      <PageHeader
        title={party?.name ?? buyer?.name ?? "Buyer"}
        back={{ to: "/app/development/buyers", label: "Buyers" }}
        subtitle={
          buyer
            ? `${buyer.sales.length === 1 ? "1 sale" : `${buyer.sales.length} sales`} · ${buyer.properties.slice(0, 3).join(", ")}${buyer.properties.length > 3 ? ` and ${buyer.properties.length - 3} more` : ""}`
            : undefined
        }
        description="Everything this buyer holds, what has been received against it and what is still due."
        actions={
          <LinkButton to={`/app/clients/${id}`} variant="secondary" need="party.view">
            Client record
          </LinkButton>
        }
      />

      {!buyer ? (
        /* Reachable by typing an address, and by opening a buyer whose only sale was just
           cancelled. A refusal that names the fix rather than one that says "not found". */
        <EmptyState
          title="No sales are recorded against this person."
          hint="They are a client rather than a buyer until a reservation or a sale exists. Record one, or open their client record."
          action={
            <LinkButton to="/app/development/sales/new" need="lease.edit">
              Record a sale
            </LinkButton>
          }
        />
      ) : (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <p className="eyebrow">{titleCase(noun.many)} held</p>
              <p className="tabular mt-1 text-2xl font-semibold">{buyer.unitsHeld}</p>
            </Card>
            <Card>
              <p className="eyebrow">Balance outstanding</p>
              <p className="tabular mt-1 text-2xl font-semibold">{fmt.money(buyer.outstanding)}</p>
            </Card>
            <Card>
              <p className="eyebrow">In arrears</p>
              <p className={`tabular mt-1 text-2xl font-semibold ${buyer.arrears > 0 ? "text-danger-600" : ""}`}>
                {fmt.money(buyer.arrears)}
              </p>
            </Card>
            <Card>
              <p className="eyebrow">Portal access</p>
              <p className="mt-1 text-sm">
                {portal.data?.linked ? (
                  <>
                    <Badge tone="success">Can sign in</Badge>
                    <span className="mt-1 block text-xs text-ink-500">{portal.data.account?.email}</span>
                  </>
                ) : (
                  <Badge tone="muted">No account</Badge>
                )}
              </p>
            </Card>
          </div>

          <Card padded={false} className="mb-6">
            <div className="border-b border-ink-200 p-4">
              <CardTitle
                hint="Every sale and every hold in this buyer's name, newest first."
                action={
                  <Link className="text-sm font-medium text-brand-700 hover:underline" to={salesLink({ buyerId: id })}>
                    Open in Sales
                  </Link>
                }
              >
                What they have bought
              </CardTitle>
            </div>
            <Table
              head={
                <tr>
                  <th>Reference</th>
                  <th>Property</th>
                  <th>{titleCase(noun.one)}</th>
                  <th>Stage</th>
                  <th className="num">Received</th>
                  <th className="num">Balance outstanding</th>
                </tr>
              }
            >
              {buyer.sales.map((sale) => (
                <tr key={`${sale.kind}-${sale.id}`}>
                  <td>
                    <Link className="font-medium text-brand-700 hover:underline" to={sale.href}>
                      {sale.reference}
                    </Link>
                  </td>
                  <td className="text-ink-600">{sale.estateName}</td>
                  <td className="text-ink-600">
                    {sale.unitLabel ?? <span className="text-ink-400">Not yet allocated</span>}
                  </td>
                  <td>
                    <StageBadge row={sale} />
                  </td>
                  <td className="num tabular">{fmt.money(sale.paid)}</td>
                  <td className="num tabular">
                    {sale.price === null ? <span className="text-ink-400">—</span> : fmt.money(sale.outstanding)}
                  </td>
                </tr>
              ))}
            </Table>
          </Card>

          <Card>
            <CardTitle>How to reach them</CardTitle>
            <div className="grid gap-4 sm:grid-cols-2">
              <Detail label="Telephone">{party?.phone ?? <span className="text-ink-400">Not recorded</span>}</Detail>
              <Detail label="Email">{party?.email ?? <span className="text-ink-400">Not recorded</span>}</Detail>
              <Detail label="Address">{party?.address ?? <span className="text-ink-400">Not recorded</span>}</Detail>
              {party?.statusName ? <Detail label="Status">{party.statusName}</Detail> : null}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
